// MUI
import { Box } from "@mui/material";

// Escape regex metacharacters
const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Build (and cache) a regex that:
 *  - Captures the target words (case-insensitive)
 *  - Matches whole words only (via \b boundaries)
 */
const buildRegex = (words) => {
  const escaped = words.map(escapeRegex);
  const pattern = `\\b(?:${escaped.join("|")})\\b`;
  return {
    split: new RegExp(`(${pattern})`, "gi"),
    test: new RegExp(`^${pattern}$`, "i"),
  };
};

// Simple memo cache
const regexCache = new Map();
const getRegex = (words) => {
  const key = words.join("||");
  if (!regexCache.has(key)) regexCache.set(key, buildRegex(words));
  return regexCache.get(key);
};

/**
 * highlightWord
 *
 * Takes a text string and a list of target words and returns an array of
 * strings / React <span> elements where each whole-word, case-insensitive
 * match of any target word is wrapped in a styled <Box>.
 *
 * Matching:
 * - Whole word only (word boundaries \b on both sides).
 * - Case-insensitive (e.g. NEW, New, new all match).
 * - Does NOT highlight substrings inside larger words (e.g. "renew" unaffected when matching "new").
 *
 * Parameters:
 * @param {string} text  The original text to process.
 * @param {string[]} [words] Words to highlight
 *
 * Returns:
 * @returns {(string|JSX.Element)[]} Pieces ready to be rendered directly in JSX.
 *
 */
const highlightWord = (text, words) => {
  if (!text) return [];
  const { split, test } = getRegex(words);
  return text.split(split).map((part, idx) =>
    test.test(part) ? (
      <Box
        component="span"
        key={idx}
        sx={{
          color: (theme) =>
            theme.palette.mode === "dark" ? "text.primary" : "primary.main",
          fontWeight: "550",
        }}
      >
        {part}
      </Box>
    ) : (
      part
    )
  );
};

export default highlightWord;
